'use client';

import React, { useState } from 'react';
import { Search, Filter, Mic, Trash2, ChevronUp, ChevronDown, MessageSquare, ListPlus, CheckCircle } from 'lucide-react';
import type { GossipRow, GossipStatus } from './GossipDetailDrawer';

export const INITIAL_GOSSIP_ROWS: GossipRow[] = [
  { id: 'admin-001', text: 'El dueño del negocio de la esquina tiene dos familias en la misma cuadra y ninguna sabe de la otra 🤯', category: 'Vecinos', votes: 567, status: 'en-cola', nickname: 'Anónima', time: '21:12', comments: [] },
  { id: 'admin-002', text: 'El marido de mi amiga dice que trabaja hasta tarde pero lo vieron en el mall con otra señora comprando ropa de mujer... para él 💅', category: 'Familia', votes: 445, status: 'contando', nickname: 'La del 4to', time: '20:58', comments: [{ id: 'c1', text: '¡Este es el mejor del día! Lo cuento primero 🔥', timestamp: '21:00' }] },
  { id: 'admin-003', text: 'Una famosa influencer de Santiago compra sus propios productos con cuentas falsas para inflar las reseñas ⭐', category: 'Famosos', votes: 389, status: 'en-cola', nickname: 'Anónima', time: '21:05', comments: [] },
  { id: 'admin-004', text: 'Una amiga se fue de vacaciones con su "primo" y resulta que el primo tiene el mismo apellido que ella pero no son familia 🤔', category: 'Amor', votes: 312, status: 'pendiente', nickname: 'Curiosa_SCL', time: '20:30', comments: [] },
  { id: 'admin-005', text: 'El presidente del condominio usa los fondos del edificio para pagar el Netflix de su casa 😤', category: 'Vecinos', votes: 267, status: 'en-cola', nickname: 'Anónima', time: '21:08', comments: [] },
  { id: 'admin-006', text: 'Mi vecina del 3ro lleva 3 semanas diciéndole a todos que está a dieta pero la pillé comprando 2 tortas en la pastelería... 👀', category: 'Vecinos', votes: 234, status: 'pendiente', nickname: 'Anónima', time: '19:45', comments: [] },
  { id: 'admin-007', text: 'El profe de yoga del barrio tiene 3 alumnas que creen que son su polola y ninguna sabe de las otras dos 🧘‍♂️', category: 'Amor', votes: 143, status: 'pendiente', nickname: 'Yogi_fan', time: '18:22', comments: [] },
  { id: 'admin-008', text: 'Mi jefe llegó a la reunión con la camisa al revés y nadie le dijo nada en toda la mañana 😂', category: 'Trabajo', votes: 189, status: 'contado', nickname: 'Empleada_secreta', time: 'ayer', comments: [] },
  { id: 'admin-009', text: 'Mi compañera de trabajo lleva 6 meses diciéndole a todos que está embarazada para no hacer nada... y no está embarazada 😱', category: 'Trabajo', votes: 156, status: 'pendiente', nickname: 'Anónima', time: '17:10', comments: [] },
  { id: 'admin-010', text: 'Mi tía lleva 10 años diciendo que viajará a Europa "el próximo año" y nunca ha salido de la Región Metropolitana ✈️', category: 'Familia', votes: 98, status: 'contado', nickname: 'Sobrina_leal', time: 'ayer', comments: [] },
  { id: 'admin-011', text: 'La nueva vecina del piso 5 lleva 3 semanas entrando y saliendo con maletas a deshoras... algo pasa ahí 🧳', category: 'Vecinos', votes: 76, status: 'pendiente', nickname: 'Anónima', time: '16:40', comments: [] },
  { id: 'admin-012', text: 'Un compañero de la oficina lleva 6 meses trabajando desde casa y nunca enciende la cámara en las reuniones... dicen que trabaja en pijama y con resaca 🍺', category: 'Trabajo', votes: 112, status: 'pendiente', nickname: 'Anónima', time: '15:55', comments: [] },
];

const STATUS_OPTIONS: { value: GossipStatus; label: string }[] = [
  { value: 'pendiente', label: '📬 Pendiente' },
  { value: 'en-cola', label: '⏳ En Cola' },
  { value: 'contando', label: '🎙️ Contando' },
  { value: 'contado', label: '✅ Contado' },
];

const statusStyles: Record<GossipStatus, string> = {
  pendiente: 'status-pendiente',
  'en-cola': 'status-en-cola',
  contando: 'status-contando',
  contado: 'status-contado',
};

const categoryColors: Record<string, string> = {
  Amor: 'bg-pink-100 text-pink-700',
  Trabajo: 'bg-blue-100 text-blue-700',
  Familia: 'bg-orange-100 text-orange-700',
  Vecinos: 'bg-green-100 text-green-700',
  Famosos: 'bg-purple-100 text-purple-700',
  Otro: 'bg-gray-100 text-gray-700',
};

interface GossipTableProps {
  rows: GossipRow[];
  onStatusChange: (id: string, status: GossipStatus) => void;
  onDelete: (id: string) => void;
  onOpenDetail: (gossip: GossipRow) => void;
}

export default function GossipTable({ rows, onStatusChange, onDelete, onOpenDetail }: GossipTableProps) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [filterCategory, setFilterCategory] = useState<string>('todas');
  const [sortField, setSortField] = useState<'votes' | 'time'>('votes');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 8;

  const handleSort = (field: 'votes' | 'time') => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = rows
    .filter((r) => {
      const matchSearch = search === '' || r.text.toLowerCase().includes(search.toLowerCase()) || r.nickname.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'todos' || r.status === filterStatus;
      const matchCat = filterCategory === 'todas' || r.category === filterCategory;
      return matchSearch && matchStatus && matchCat;
    })
    .sort((a, b) => {
      if (sortField === 'votes') return sortDir === 'desc' ? b.votes - a.votes : a.votes - b.votes;
      return 0;
    });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
      {/* Table header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 px-6 py-5 border-b border-border">
        <div>
          <h3 className="font-bold text-foreground text-lg">Gestión de Chismes</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filtered.length} chismes encontrados · Haz clic en un chisme para ver detalles y comentar
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar chisme o apodo..."
              className="w-full bg-input border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-muted-foreground shrink-0" />
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="bg-card border border-border rounded-xl px-3 py-2.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="todos">Todos los estados</option>
              <option value="pendiente">Pendientes</option>
              <option value="en-cola">En Cola</option>
              <option value="contando">Contando</option>
              <option value="contado">Contados</option>
            </select>
          </div>

          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
            className="bg-card border border-border rounded-xl px-3 py-2.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="todas">Todas las categorías</option>
            {['Amor', 'Trabajo', 'Familia', 'Vecinos', 'Famosos', 'Otro'].map((cat) => (
              <option key={`filter-cat-${cat}`} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-muted/40 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-8">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Chisme</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-28">Categoría</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-24">Apodo</th>
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-24 cursor-pointer hover:text-primary transition-colors select-none"
                onClick={() => handleSort('votes')}
              >
                <span className="flex items-center gap-1">
                  Votos
                  {sortField === 'votes' ? (
                    sortDir === 'desc' ? <ChevronDown size={13} className="text-primary" /> : <ChevronUp size={13} className="text-primary" />
                  ) : (
                    <ChevronDown size={13} className="opacity-30" />
                  )}
                </span>
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-36">Estado</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-20">Hora</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16">
                  <div className="text-4xl mb-3">🤷‍♀️</div>
                  <p className="font-semibold text-foreground mb-1">No hay chismes aquí</p>
                  <p className="text-sm text-muted-foreground">Cambia los filtros para ver más</p>
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <tr
                  key={row.id}
                  className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${
                    row.status === 'contando' ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="px-4 py-4 text-xs text-muted-foreground font-tabular">
                    {(page - 1) * PER_PAGE + i + 1}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => onOpenDetail(row)}
                      className="text-left group"
                    >
                      <p className="text-sm text-foreground leading-snug line-clamp-2 max-w-sm group-hover:text-primary transition-colors">
                        {row.text}
                      </p>
                      {(row.comments?.length ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-primary font-medium">
                          <MessageSquare size={10} />
                          {row.comments?.length} nota{(row.comments?.length ?? 0) > 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[row.category] || categoryColors['Otro']}`}>
                      {row.category}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground font-medium">{row.nickname}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-bold font-tabular text-foreground flex items-center gap-1">
                      <ChevronUp size={13} className="text-primary" />
                      {row.votes}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={row.status}
                      onChange={(e) => onStatusChange(row.id, e.target.value as GossipStatus)}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-xl border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 transition ${statusStyles[row.status as GossipStatus]}`}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={`status-opt-${opt.value}`} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground font-tabular">{row.time}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onStatusChange(row.id, 'en-cola')}
                        disabled={row.status === 'en-cola' || row.status === 'contando' || row.status === 'contado'}
                        title="Agregar a la cola"
                        className="p-2 rounded-lg text-muted-foreground hover:text-secondary hover:bg-secondary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        aria-label="Agregar a cola"
                      >
                        <ListPlus size={15} />
                      </button>

                      <button
                        onClick={() => onStatusChange(row.id, 'contando')}
                        disabled={row.status === 'contando' || row.status === 'contado'}
                        title="Contar ahora en vivo"
                        className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        aria-label="Contar ahora"
                      >
                        <Mic size={15} />
                      </button>

                      <button
                        onClick={() => onStatusChange(row.id, 'contado')}
                        disabled={row.status === 'contado'}
                        title="Marcar como contado"
                        className="p-2 rounded-lg text-muted-foreground hover:text-green-600 hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        aria-label="Marcar contado"
                      >
                        <CheckCircle size={15} />
                      </button>

                      <button
                        onClick={() => onOpenDetail(row)}
                        title="Ver detalles y comentar"
                        className="p-2 rounded-lg text-muted-foreground hover:text-secondary hover:bg-secondary/10 transition-all"
                        aria-label="Ver y comentar"
                      >
                        <MessageSquare size={15} />
                      </button>

                      {deleteConfirm === row.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { onDelete(row.id); setDeleteConfirm(null); }}
                            className="text-xs bg-red-500 text-white px-2 py-1 rounded-lg font-semibold hover:bg-red-600 transition-colors"
                          >
                            Sí
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-lg font-semibold hover:bg-border transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(row.id)}
                          title="Eliminar chisme"
                          className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all"
                          aria-label="Eliminar chisme"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} de {filtered.length} chismes
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground border border-border hover:text-primary hover:border-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                  page === p
                    ? 'bg-primary text-white' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground border border-border hover:text-primary hover:border-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}